import { apiRequest } from '@/lib/api/client';
import type {
  ColabRunStatsOut,
  ContentFilters,
  ContentHomeOut,
  ContentOverviewOut,
  HashtagSubscriptionOut,
  ModerationQueueOut,
  ModerationTaskOut,
  PublicContentDetail,
  PublicContentListOut,
  PublicPageListItem,
  SocialHandoffOut,
  SourceRegistryOut,
} from './types';

const buildQuery = (filters: ContentFilters & Record<string, unknown>): Record<string, string | number | undefined> => {
  const result: Record<string, string | number | undefined> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (val !== undefined && val !== '' && val !== null) {
      result[key] = val as string | number;
    }
  }
  return result;
};

export const portalApi = {
  home: (): Promise<ContentHomeOut> =>
    apiRequest<ContentHomeOut>('/public/home', { channel: 'platform' }),

  list: (filters: ContentFilters): Promise<PublicContentListOut> =>
    apiRequest<PublicContentListOut>('/public/content', {
      channel: 'platform',
      query: buildQuery(filters as Record<string, unknown>),
    }),

  detail: (id: string): Promise<PublicContentDetail> =>
    apiRequest<PublicContentDetail>(`/public/content/${id}`, { channel: 'platform' }),

  toggleFavorite: (contentId: string): Promise<{ content_id: string; is_saved: boolean; save_count: number }> =>
    apiRequest(`/public/favorites/${contentId}`, { method: 'POST', channel: 'platform' }),

  votePoll: (contentId: string, optionIndices: number[]): Promise<unknown> =>
    apiRequest(`/public/content/${contentId}/vote`, {
      method: 'POST',
      channel: 'platform',
      body: { option_indices: optionIndices },
    }),

  myContent: (page = 1, pageSize = 20): Promise<PublicContentListOut> =>
    apiRequest<PublicContentListOut>('/public/my-content', {
      channel: 'platform',
      query: { page, page_size: pageSize },
    }),

  createDraft: (payload: unknown): Promise<PublicContentDetail> =>
    apiRequest<PublicContentDetail>('/public/content', {
      method: 'POST',
      channel: 'platform',
      body: payload,
    }),

  updateDraft: (id: string, payload: unknown): Promise<PublicContentDetail> =>
    apiRequest<PublicContentDetail>(`/public/content/${id}`, {
      method: 'PATCH',
      channel: 'platform',
      body: payload,
    }),

  submitForModeration: (id: string): Promise<PublicContentDetail> =>
    apiRequest<PublicContentDetail>(`/public/content/${id}/submit`, {
      method: 'POST',
      channel: 'platform',
    }),

  publishContent: (id: string): Promise<PublicContentDetail> =>
    apiRequest<PublicContentDetail>(`/public/content/${id}/publish`, {
      method: 'POST',
      channel: 'platform',
    }),

  getPage: (slug: string): Promise<{ content_blocks: Array<{ block_id: string; block_type: string; content: Record<string, unknown>; order: number }> }> =>
    apiRequest(`/public/pages/${slug}`, { channel: 'none' }),

  listPages: (): Promise<PublicPageListItem[]> =>
    apiRequest<PublicPageListItem[]>('/public/pages', { channel: 'platform' }),

  savePageDraft: (slug: string, title: string, body: string, pageType: string): Promise<unknown> =>
    apiRequest(`/public/pages/${slug}/revisions?page_type=${pageType}`, {
      method: 'POST',
      channel: 'platform',
      body: {
        content_blocks: [
          { block_id: 'main-title', block_type: 'heading', content: { title }, order: 0 },
          { block_id: 'main-body', block_type: 'paragraph', content: { text: body }, order: 1 },
        ],
      },
    }),

  publishPage: (slug: string): Promise<unknown> =>
    apiRequest(`/public/pages/${slug}/publish`, { method: 'POST', channel: 'platform' }),

  contentOverview: (): Promise<ContentOverviewOut> =>
    apiRequest<ContentOverviewOut>('/public/content/overview', { channel: 'platform' }),

  manageContent: (params?: { status_filter?: string; kind_filter?: string; page?: number }): Promise<PublicContentListOut> =>
    apiRequest<PublicContentListOut>('/public/content/manage', {
      channel: 'platform',
      query: params,
    }),

  listHashtagSubscriptions: (): Promise<HashtagSubscriptionOut[]> =>
    apiRequest<HashtagSubscriptionOut[]>('/public/hashtags/subscriptions', { channel: 'platform' }),

  subscribeHashtag: (tag: string): Promise<HashtagSubscriptionOut> =>
    apiRequest<HashtagSubscriptionOut>('/public/hashtags/subscribe', {
      method: 'POST',
      channel: 'platform',
      body: { tag },
    }),

  unsubscribeHashtag: (tag: string): Promise<{ ok: boolean }> =>
    apiRequest<{ ok: boolean }>(`/public/hashtags/subscribe/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
      channel: 'platform',
    }),

  getModerationQueue: (params?: { status_filter?: string; page?: number }): Promise<ModerationQueueOut> =>
    apiRequest<ModerationQueueOut>('/public/moderation', {
      channel: 'platform',
      query: { status_filter: params?.status_filter, page: params?.page },
    }),

  moderationDecision: (taskId: string, decision: string, comment?: string): Promise<ModerationTaskOut> =>
    apiRequest<ModerationTaskOut>(`/public/moderation/${taskId}/decision`, {
      method: 'POST',
      channel: 'platform',
      body: { decision, comment },
    }),

  listSources: (): Promise<SourceRegistryOut[]> =>
    apiRequest<SourceRegistryOut[]>('/public/sources', { channel: 'platform' }),

  createSource: (payload: unknown): Promise<SourceRegistryOut> =>
    apiRequest<SourceRegistryOut>('/public/sources', {
      method: 'POST',
      channel: 'platform',
      body: payload,
    }),

  updateSourceStatus: (sourceId: string, newStatus: string): Promise<SourceRegistryOut> =>
    apiRequest<SourceRegistryOut>(`/public/sources/${sourceId}/status?new_status=${newStatus}`, {
      method: 'PATCH',
      channel: 'platform',
    }),

  testSource: (sourceId: string): Promise<{ success: boolean; http_status?: number; error?: string; fetch_duration_ms?: number }> =>
    apiRequest(`/public/sources/${sourceId}/test`, { method: 'POST', channel: 'platform' }),

  colabStats: (): Promise<ColabRunStatsOut> =>
    apiRequest<ColabRunStatsOut>('/public/autoscan/stats', { channel: 'platform' }),

  colabRunNow: (): Promise<ColabRunStatsOut> =>
    apiRequest<ColabRunStatsOut>('/public/autoscan/run', { method: 'POST', channel: 'platform' }),

  createHandoff: (payload: unknown): Promise<SocialHandoffOut> =>
    apiRequest<SocialHandoffOut>('/public/social-handoff', {
      method: 'POST',
      channel: 'platform',
      body: payload,
    }),

  updateHandoff: (id: string, payload: unknown): Promise<SocialHandoffOut> =>
    apiRequest<SocialHandoffOut>(`/public/social-handoff/${id}`, {
      method: 'PATCH',
      channel: 'platform',
      body: payload,
    }),

  listHandoffs: (params?: { mine_only?: boolean; status_filter?: string }): Promise<SocialHandoffOut[]> =>
    apiRequest<SocialHandoffOut[]>('/public/social-handoff', {
      channel: 'platform',
      query: { mine_only: params?.mine_only ? 'true' : undefined, status_filter: params?.status_filter },
    }),
};
